// ChecklistItemActions.js
// Canonical action objects on checklist template / workflow recipe items.

import { createEnumOptionId } from 'utilities/EnumOptions';

export const CHECKLIST_ITEM_ACTION_TYPES = Object.freeze({
  SET_FIELD: 'setField',
  TAKE_PHOTO: 'takePhoto',
  RECORD_VIDEO: 'recordVideo',
  UPLOAD_FILES: 'uploadFiles',
  ADD_NOTE: 'addNote',
  CREATE_SKETCH: 'createSketch',
  SCHEDULE_EVENT: 'scheduleEvent',
  ADD_LINK: 'addLink',
});

/** Capture / upload actions that attach media children to the TASK. */
export const CHECKLIST_MEDIA_ACTION_TYPES = Object.freeze([
  CHECKLIST_ITEM_ACTION_TYPES.TAKE_PHOTO,
  CHECKLIST_ITEM_ACTION_TYPES.RECORD_VIDEO,
  CHECKLIST_ITEM_ACTION_TYPES.UPLOAD_FILES,
]);

/** Seed a related record on the TASK and open Peek. */
export const CHECKLIST_RECORD_ACTION_TYPES = Object.freeze([
  CHECKLIST_ITEM_ACTION_TYPES.ADD_NOTE,
  CHECKLIST_ITEM_ACTION_TYPES.CREATE_SKETCH,
  CHECKLIST_ITEM_ACTION_TYPES.SCHEDULE_EVENT,
  CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK,
]);

/** All non-field actions that target the TASK (media + records). */
export const CHECKLIST_TASK_ATTACH_ACTION_TYPES = Object.freeze([
  ...CHECKLIST_MEDIA_ACTION_TYPES,
  ...CHECKLIST_RECORD_ACTION_TYPES,
]);

const TASK_ATTACH_ACTION_TYPE_SET = new Set(CHECKLIST_TASK_ATTACH_ACTION_TYPES);
const MEDIA_ACTION_TYPE_SET = new Set(CHECKLIST_MEDIA_ACTION_TYPES);
const RECORD_ACTION_TYPE_SET = new Set(CHECKLIST_RECORD_ACTION_TYPES);

const ACTION_DEFAULT_LABELS = Object.freeze({
  [CHECKLIST_ITEM_ACTION_TYPES.TAKE_PHOTO]: 'Take Photo',
  [CHECKLIST_ITEM_ACTION_TYPES.RECORD_VIDEO]: 'Record Video',
  [CHECKLIST_ITEM_ACTION_TYPES.UPLOAD_FILES]: 'Upload Files',
  [CHECKLIST_ITEM_ACTION_TYPES.ADD_NOTE]: 'Add Notes',
  [CHECKLIST_ITEM_ACTION_TYPES.CREATE_SKETCH]: 'Add Sketch',
  [CHECKLIST_ITEM_ACTION_TYPES.SCHEDULE_EVENT]: 'Add Event',
  [CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK]: 'Add Link',
});

const ACTION_ICON_KEYS = Object.freeze({
  [CHECKLIST_ITEM_ACTION_TYPES.TAKE_PHOTO]: 'photo_camera',
  [CHECKLIST_ITEM_ACTION_TYPES.RECORD_VIDEO]: 'videocam',
  [CHECKLIST_ITEM_ACTION_TYPES.UPLOAD_FILES]: 'attach_file',
  [CHECKLIST_ITEM_ACTION_TYPES.ADD_NOTE]: 'sticky_note_2',
  [CHECKLIST_ITEM_ACTION_TYPES.CREATE_SKETCH]: 'draw',
  [CHECKLIST_ITEM_ACTION_TYPES.SCHEDULE_EVENT]: 'event',
  [CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK]: 'link',
});

const normalizeString = (value) => String(value || '').trim();

/**
 * @param {unknown} type
 * @returns {boolean}
 */
export function isChecklistTaskAttachActionType(type) {
  return TASK_ATTACH_ACTION_TYPE_SET.has(normalizeString(type));
}

/**
 * @param {unknown} type
 * @returns {boolean}
 */
export function isChecklistMediaActionType(type) {
  return MEDIA_ACTION_TYPE_SET.has(normalizeString(type));
}

/**
 * @param {unknown} type
 * @returns {boolean}
 */
export function isChecklistRecordActionType(type) {
  return RECORD_ACTION_TYPE_SET.has(normalizeString(type));
}

/**
 * @param {unknown} type
 * @returns {boolean}
 */
export function isChecklistSetFieldActionType(type) {
  return normalizeString(type) === CHECKLIST_ITEM_ACTION_TYPES.SET_FIELD;
}

/**
 * @param {object|null|undefined} action
 * @returns {string}
 */
export function getChecklistActionDefaultLabel(action) {
  const type = normalizeString(action?.type);
  if (type === CHECKLIST_ITEM_ACTION_TYPES.SET_FIELD) {
    const fieldKey = normalizeString(action?.fieldKey);
    return fieldKey ? `Set ${fieldKey}` : 'Set Field';
  }
  return ACTION_DEFAULT_LABELS[type] || 'Action';
}

/**
 * @param {object|null|undefined} action
 * @returns {string}
 */
export function getChecklistActionIconKey(action) {
  const type = normalizeString(action?.type);
  if (type === CHECKLIST_ITEM_ACTION_TYPES.SET_FIELD) return 'edit';
  return ACTION_ICON_KEYS[type] || 'bolt';
}

/**
 * @param {unknown} actions
 * @returns {Array<object>}
 */
export function normalizeChecklistItemActions(actions) {
  if (!Array.isArray(actions)) return [];

  const result = [];
  const seenIds = new Set();
  const seenFieldKeys = new Set();
  const seenAttachTypes = new Set();

  actions.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object') return;

    const type = normalizeString(raw.type) || CHECKLIST_ITEM_ACTION_TYPES.SET_FIELD;

    let id = normalizeString(raw.id);
    if (!id || seenIds.has(id)) {
      id = createEnumOptionId();
    }

    if (type === CHECKLIST_ITEM_ACTION_TYPES.SET_FIELD) {
      const fieldKey = normalizeString(raw.fieldKey || raw.field || raw.name);
      if (!fieldKey || seenFieldKeys.has(fieldKey)) return;

      seenIds.add(id);
      seenFieldKeys.add(fieldKey);

      const label = normalizeString(raw.label);
      const requireValue = raw.requireValue === true;
      result.push({
        id,
        type: CHECKLIST_ITEM_ACTION_TYPES.SET_FIELD,
        fieldKey,
        ...(label ? { label } : {}),
        ...(requireValue ? { requireValue: true } : {}),
        sortOrder: Number.isFinite(Number(raw.sortOrder)) ? Number(raw.sortOrder) : index,
      });
      return;
    }

    if (!isChecklistTaskAttachActionType(type)) return;
    if (seenAttachTypes.has(type)) return;

    seenIds.add(id);
    seenAttachTypes.add(type);

    const label = normalizeString(raw.label) || ACTION_DEFAULT_LABELS[type];
    const defaultTitle = type === CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK
      ? normalizeString(raw.defaultTitle)
      : '';
    result.push({
      id,
      type,
      label,
      ...(defaultTitle ? { defaultTitle } : {}),
      sortOrder: Number.isFinite(Number(raw.sortOrder)) ? Number(raw.sortOrder) : index,
    });
  });

  return result
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((action, index) => ({
      ...action,
      sortOrder: index,
    }));
}

/**
 * @param {object} params
 * @param {string} params.fieldKey
 * @param {string} [params.fieldLabel]
 * @param {string} [params.id]
 * @param {boolean} [params.requireValue]
 * @returns {object|null}
 */
export function buildSetFieldChecklistAction({
  fieldKey,
  fieldLabel = '',
  id = '',
  requireValue = false,
} = {}) {
  const nextFieldKey = normalizeString(fieldKey);
  if (!nextFieldKey) return null;

  const label = normalizeString(fieldLabel)
    ? `Set ${normalizeString(fieldLabel)}`
    : '';

  return {
    id: normalizeString(id) || createEnumOptionId(),
    type: CHECKLIST_ITEM_ACTION_TYPES.SET_FIELD,
    fieldKey: nextFieldKey,
    ...(label ? { label } : {}),
    ...(requireValue === true ? { requireValue: true } : {}),
  };
}

/**
 * Returns whether a host-record field value counts as present for requireValue gates.
 * Matches automation-style is_not_empty: null / '' / [] / empty date-range = empty;
 * $0, selected enums, and assigned people count as present.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isChecklistHostFieldValuePresent(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (typeof value === 'object') {
    // Date / datetime range fields.
    if (
      Object.prototype.hasOwnProperty.call(value, 'startDate')
      || Object.prototype.hasOwnProperty.call(value, 'endDate')
    ) {
      return value.startDate != null || value.endDate != null;
    }
    // Money and other structured values are present when the object is non-empty.
    return Object.keys(value).length > 0;
  }
  return true;
}

/**
 * First requireValue setField action whose host field is empty, or null.
 *
 * @param {object} params
 * @param {Array<object>} [params.actions]
 * @param {function} params.getFieldValue - (fieldKey) => unknown
 * @returns {object|null}
 */
export function findMissingRequiredSetFieldAction({
  actions = [],
  getFieldValue,
} = {}) {
  if (typeof getFieldValue !== 'function') return null;
  const list = normalizeChecklistItemActions(actions);

  for (let index = 0; index < list.length; index += 1) {
    const action = list[index];
    if (!isChecklistSetFieldActionType(action?.type)) continue;
    if (action.requireValue !== true) continue;
    const fieldKey = normalizeString(action.fieldKey);
    if (!fieldKey) continue;
    if (!isChecklistHostFieldValuePresent(getFieldValue(fieldKey))) {
      return action;
    }
  }

  return null;
}

/**
 * Suggest a LINK node title from an Add Link button label
 * (e.g. "Add Docusign URL" → "Docusign URL").
 *
 * @param {unknown} buttonLabel
 * @returns {string}
 */
export function suggestChecklistAddLinkDefaultTitle(buttonLabel) {
  const label = normalizeString(buttonLabel);
  if (!label) return '';
  const stripped = label.replace(/^add\s+/i, '').trim();
  return stripped || label;
}

/**
 * @param {object} params
 * @param {string} params.type
 * @param {string} [params.id]
 * @param {string} [params.label]
 * @param {string} [params.defaultTitle] - Add Link only: prefilled LINK node title
 * @returns {object|null}
 */
export function buildTaskAttachChecklistAction({
  type,
  id = '',
  label = '',
  defaultTitle = '',
} = {}) {
  const nextType = normalizeString(type);
  if (!isChecklistTaskAttachActionType(nextType)) return null;

  const nextLabel = normalizeString(label) || ACTION_DEFAULT_LABELS[nextType];
  const nextDefaultTitle = nextType === CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK
    ? normalizeString(defaultTitle)
    : '';

  return {
    id: normalizeString(id) || createEnumOptionId(),
    type: nextType,
    label: nextLabel,
    ...(nextDefaultTitle ? { defaultTitle: nextDefaultTitle } : {}),
  };
}

/** @deprecated Use buildTaskAttachChecklistAction */
export function buildMediaChecklistAction(params) {
  return buildTaskAttachChecklistAction(params);
}

/**
 * Authoring menu options for media / capture actions.
 *
 * @returns {Array<{ type: string, label: string, iconKey: string }>}
 */
export function getChecklistMediaActionMenuOptions() {
  return CHECKLIST_MEDIA_ACTION_TYPES.map((type) => ({
    type,
    label: ACTION_DEFAULT_LABELS[type],
    iconKey: ACTION_ICON_KEYS[type],
  }));
}

/**
 * Authoring menu options for NOTE / EVENT / LINK style record actions.
 *
 * @returns {Array<{ type: string, label: string, iconKey: string }>}
 */
export function getChecklistRecordActionMenuOptions() {
  return CHECKLIST_RECORD_ACTION_TYPES.map((type) => ({
    type,
    label: ACTION_DEFAULT_LABELS[type],
    iconKey: ACTION_ICON_KEYS[type],
  }));
}

/**
 * Authoring menu options for all TASK-attach action buttons (media + records).
 *
 * @returns {Array<{ type: string, label: string, iconKey: string }>}
 */
export function getChecklistTaskAttachActionMenuOptions() {
  return CHECKLIST_TASK_ATTACH_ACTION_TYPES.map((type) => ({
    type,
    label: ACTION_DEFAULT_LABELS[type],
    iconKey: ACTION_ICON_KEYS[type],
  }));
}
